import type { PolygonLayer } from './editor-feature';

/**
 * What a click on the surface does.
 *
 * A placement tool reverts to Select as soon as it has placed something: adding a dozen
 * desks in a row is rare, and a tool that silently stays armed turns the next click meant
 * as a selection into another desk.
 *
 * The drawing tools are the exception. A polygon takes many clicks by definition, so those
 * stay armed until Esc or another tool.
 */
export type EditorTool =
  | 'select'
  | 'vertex'
  | 'desk'
  | 'deskGroup'
  | 'deskShape'
  | 'text'
  | 'icon'
  | 'polygon'
  | 'rectangle'
  | 'ellipse'
  | 'square'
  | 'circle'
  | 'triangle'
  | 'line';

export const TOOL_SHORTCUTS: Readonly<Record<string, EditorTool>> = {
  v: 'select',
  e: 'vertex',
  d: 'desk',
  b: 'deskGroup',
  s: 'deskShape',
  t: 'text',
  i: 'icon',
  p: 'polygon',
  r: 'rectangle',
  c: 'ellipse',
};

/**
 * Whether a tool edits the points of a shape rather than the shape as a whole.
 *
 * Its own mode because the two cannot share a click: on the select tool a press inside a
 * shape starts a drag of the whole thing, and on this one a press near its edge has to mean
 * the point under the cursor.
 */
export const isVertexTool = (tool: EditorTool): boolean => tool === 'vertex';

/** Tools that place one thing and hand back to Select. */
export const isPlacementTool = (tool: EditorTool): boolean => (
  tool === 'desk' || tool === 'deskGroup' || tool === 'deskShape' || tool === 'text' || tool === 'icon'
);

/**
 * Tools that draw a shape between two points: a click for one corner, a click for the other.
 *
 * Every basic shape is the same gesture and differs only in what is drawn inside the box —
 * a line being the degenerate case, drawn from the first click to the second.
 */
export type CorneredTool = 'rectangle' | 'ellipse' | 'square' | 'circle' | 'triangle' | 'line';

const CORNERED_TOOLS: ReadonlySet<EditorTool> = new Set<EditorTool>([
  'rectangle', 'ellipse', 'square', 'circle', 'triangle', 'line',
]);

export const isCorneredTool = (tool: EditorTool): tool is CorneredTool => CORNERED_TOOLS.has(tool);

/** Tools that build geometry over several clicks or a drag. */
export const isDrawingTool = (tool: EditorTool): boolean => tool === 'polygon' || isCorneredTool(tool);

/**
 * Whether a tool needs a scale before it can do anything useful.
 *
 * A desk group's every dimension is in metres — desk size, the gap between desks, the aisle —
 * so on an unmeasured plan there is no arrangement to lay out, and guessing one produces a
 * block of desks a fifth of a millimetre wide on a plan in millimetres.
 */
export const needsScale = (tool: EditorTool): boolean => tool === 'deskGroup';

/**
 * Which layer a drawn area lands on.
 *
 * Chosen by the admin rather than by the tool, because the same rectangle is a room on one
 * layer and a wall on another — so the tool says what shape, and the target says where.
 */
export type DrawTarget = PolygonLayer;
